/**
 * appointmentUtils.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Utilitários de agendamento para o fluxo v2.
 * Funções puras — sem efeitos colaterais, sem dependências de estado React.
 *
 * @module appointmentUtils
 */

import {
  Available,
  Scheduled,
  Rescheduled,
  Confirmed,
  ConfirmedByPatient,
  Waiting,
  Doing,
  Done,
  Canceled,
  CanceledByPatient,
  Missed,
  Blocked,
} from '../../../../consts/AppointmentsState';
import { getStatusColors } from '../../../../consts/AppointmentStatusColors';
import { isSameDay, parseAppointmentDate } from './calendarUtils';

// ── Labels de status ──────────────────────────────────────────────────────────

/** @type {Record<string, string>} Mapa de status → label em pt-BR */
const STATUS_LABELS = {
  [Available]:          'Disponível',
  [Scheduled]:          'Agendado',
  [Rescheduled]:        'Reagendado',
  [Confirmed]:          'Confirmado',
  [ConfirmedByPatient]: 'Confirmado pelo paciente',
  [Waiting]:            'Aguardando no local',
  [Doing]:              'Em atendimento',
  [Done]:               'Realizado',
  [Canceled]:           'Cancelado',
  [CanceledByPatient]:  'Cancelado pelo paciente',
  [Missed]:             'Falta',
  [Blocked]:            'Bloqueado',
};

// ── Funções de status ─────────────────────────────────────────────────────────

/**
 * Retorna o label legível do status em pt-BR.
 *
 * @param {string} status - ID do status
 * @returns {string} Label do status
 */
export function getStatusLabel(status) {
  return STATUS_LABELS[status] || status;
}

/**
 * Verifica se o status é "ativo" — não cancelado, não bloqueado, não falta.
 * Status ativos são aqueles que representam um agendamento em andamento.
 *
 * @param {string} status - ID do status
 * @returns {boolean}
 */
export function isActiveStatus(status) {
  return ![Canceled, CanceledByPatient, Missed, Blocked, Available].includes(status);
}

/**
 * Verifica se o status bloqueia o horário para fins de verificação de conflitos.
 * Horários bloqueados não permitem novos agendamentos no mesmo slot.
 *
 * @param {string} status - ID do status
 * @returns {boolean}
 */
export function isBlockingStatus(status) {
  // NonBlockers de AppointmentsState: Available, Rescheduled, Canceled, CanceledByPatient, Missed
  return ![Available, Rescheduled, Canceled, CanceledByPatient, Missed].includes(status);
}

/**
 * Retorna a cor de fundo do card baseado no status.
 * Usa AppointmentStatusColors como fonte de verdade.
 *
 * @param {string} status - ID do status
 * @returns {string} Cor CSS (hex)
 */
export function getCardBackground(status) {
  const colors = getStatusColors(status);
  return colors.bg;
}

// ── Filtros de agendamentos ───────────────────────────────────────────────────

/**
 * Filtra agendamentos por data (mesmo dia).
 *
 * @param {Array<{ date: string }>} appointments - Lista de agendamentos
 * @param {Date} date - Data alvo
 * @returns {Array} Agendamentos do dia
 */
export function filterByDate(appointments, date) {
  return appointments.filter(apt => {
    const aptDate = parseAppointmentDate(apt.date);
    return isSameDay(aptDate, date);
  });
}

/**
 * Filtra agendamentos por profissional (resource).
 *
 * @param {Array<{ resource: string }>} appointments - Lista de agendamentos
 * @param {string} resourceId - ID do profissional
 * @returns {Array} Agendamentos do profissional
 */
export function filterByResource(appointments, resourceId) {
  if (!resourceId) return appointments;
  return appointments.filter(apt => apt.resource === resourceId);
}

/**
 * Filtra agendamentos por sala.
 *
 * @param {Array<{ roomId: string | null }>} appointments - Lista de agendamentos
 * @param {string} roomId - ID da sala
 * @returns {Array} Agendamentos da sala
 */
export function filterByRoom(appointments, roomId) {
  if (!roomId) return appointments;
  return appointments.filter(apt => apt.roomId === roomId);
}

/**
 * Retorna agendamentos de um slot específico (hora e minuto exatos).
 *
 * @param {Array<{ date: string }>} appointments - Lista de agendamentos
 * @param {Date} date - Data do slot
 * @param {number} hour - Hora do slot
 * @param {number} minute - Minuto do slot
 * @returns {Array} Agendamentos no slot
 */
export function getAppointmentsForSlot(appointments, date, hour, minute) {
  return appointments.filter(apt => {
    const aptDate = parseAppointmentDate(apt.date);
    return (
      isSameDay(aptDate, date) &&
      aptDate.getHours() === hour &&
      aptDate.getMinutes() === minute
    );
  });
}

/**
 * Verifica se um slot está disponível (sem conflitos de agendamentos bloqueantes).
 *
 * @param {Array<{ date: string, duration: number, status: string }>} appointments
 * @param {Date} date - Data do slot
 * @param {number} hour - Hora do slot
 * @param {number} minute - Minuto do slot
 * @param {number} [durationMinutes=50] - Duração do novo agendamento em minutos
 * @returns {boolean} True se o slot estiver disponível
 */
export function isSlotAvailable(appointments, date, hour, minute, durationMinutes = 50) {
  const slotStart = new Date(date);
  slotStart.setHours(hour, minute, 0, 0);
  const slotEnd = new Date(slotStart.getTime() + durationMinutes * 60 * 1000);

  const dayAppointments = filterByDate(appointments, date);

  return !dayAppointments.some(apt => {
    if (!isBlockingStatus(apt.status)) return false;

    const aptStart = parseAppointmentDate(apt.date);
    const aptEnd = new Date(aptStart.getTime() + apt.duration * 60 * 1000);

    return slotStart < aptEnd && aptStart < slotEnd;
  });
}

// ── Formatação ────────────────────────────────────────────────────────────────

/**
 * Formata duração em minutos para string legível.
 * Exemplos: "50 min", "1h", "1h 30min"
 *
 * @param {number} minutes - Duração em minutos
 * @returns {string} Duração formatada
 */
export function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}min`;
}

/**
 * Formata o preço em reais (BRL).
 * Exemplo: "R$ 150,00"
 *
 * @param {number} price - Preço em centavos ou reais
 * @returns {string} Preço formatado
 */
export function formatPrice(price) {
  if (price == null || isNaN(price)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(price);
}

/**
 * Retorna as iniciais de um nome (até 2 letras).
 * Exemplo: "João Silva" → "JS"
 *
 * @param {string} name - Nome completo
 * @returns {string} Iniciais
 */
export function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Separa o primeiro nome do resto ("Roberta Lima Souza" → ["Roberta", "Lima Souza"]),
 * para os cards da agenda darem prioridade ao primeiro nome quando falta espaço.
 */
export function splitFirstName(name) {
  const trimmed = (name || '').trim();
  const index = trimmed.search(/\s/);
  if (index === -1) return [trimmed, ''];
  return [trimmed.slice(0, index), trimmed.slice(index).trim()];
}

/**
 * Ordena agendamentos por hora de início (crescente).
 *
 * @param {Array<{ date: string }>} appointments
 * @returns {Array} Agendamentos ordenados
 */
export function sortByTime(appointments) {
  return [...appointments].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
}

/**
 * Converte a duração de uma linha de procedimento em minutos.
 * Aceita "HH:MM:SS", "HH:MM", número ou string numérica.
 *
 * @param {string|number} raw
 * @returns {number} Minutos (0 se inválido)
 */
export function durationToMinutes(raw) {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number') return raw;
  const str = String(raw).trim();
  if (str.includes(':')) {
    const parts = str.split(':').map(Number);
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  }
  const n = Number(str);
  return isNaN(n) ? 0 : n;
}

/**
 * Minutos → "HH:MM:SS", o formato que a API grava.
 *
 * @param {number} minutes
 * @returns {string}
 */
export function minutesToDuration(minutes) {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}

/**
 * Ids dos procedimentos de um agendamento: as linhas `procedures` quando
 * existem, senão o procedimento principal (agendamentos antigos).
 *
 * @param {object} appointment
 * @returns {Array<string>} Ids como string, na ordem gravada
 */
export function appointmentProcedureIds(appointment) {
  const lines = Array.isArray(appointment?.procedures) ? appointment.procedures : [];
  if (lines.length > 0) {
    return lines.map((line) => String(line.procedure_id ?? line.id ?? '')).filter(Boolean);
  }
  const single = appointment?.procedure;
  return single != null && single !== '' && String(single) !== '0' ? [String(single)] : [];
}

/**
 * Nome dos procedimentos do agendamento para a agenda ("Axila + Virilha + Pernas").
 * Procedimentos sem cadastro (apagados) ficam de fora.
 *
 * @param {object} appointment
 * @param {Array<{Id, Label}>} procedures - CompanyConfig.Procedures
 * @returns {string} '' quando não há nenhum
 */
export function procedureLabels(appointment, procedures) {
  const list = Array.isArray(procedures) ? procedures : [];
  return appointmentProcedureIds(appointment)
    .map((id) => list.find((p) => String(p.Id) === id)?.Label)
    .filter(Boolean)
    .join(' + ');
}

/**
 * Soma das linhas de procedimento: duração em minutos e valor.
 *
 * @param {Array<{duration, price}>} lines
 * @returns {{ minutes: number, price: number }}
 */
export function sumProcedures(lines) {
  return (Array.isArray(lines) ? lines : []).reduce(
    (acc, line) => ({
      minutes: acc.minutes + durationToMinutes(line.duration),
      price: Math.round((acc.price + (Number(line.price) || 0)) * 100) / 100,
    }),
    { minutes: 0, price: 0 }
  );
}
